// navigation-completion Stage F — a real in-browser PDF renderer for the
// navigation editing workspace.
//
// WHY this exists: the old preview iframed the exact-revision blob and relied on
// the host browser's built-in PDF viewer. The Codex in-app browser has none, so
// the frame was blank — the reviewer could not see where a bookmark/link points
// while editing. This component rasterises the EXACT bytes with pdf.js onto a
// <canvas>, so it shows real pixels in any browser.
//
// Hard safety rules (Kimi review):
//   • The worker is BUNDLED via Vite `?url` (no CDN, no network worker).
//   • `isEvalSupported:false` and scripting is NEVER enabled, so embedded PDF
//     actions / JavaScript are not executed and URI/Launch actions are never
//     auto-followed — we only ever RENDER pages and RESOLVE named destinations.
//   • pdf.js is imported LAZILY (dynamic import) so it stays out of the main
//     bundle; it loads only when a reviewer actually opens the workspace.
//   • Every document load and page render is cancellable: a superseded load
//     invalidates its epoch, aborts its fetch, destroys the in-flight
//     PDFDocumentLoadingTask and any resolved PDFDocumentProxy, and a superseded
//     render is cancelled — so switching document/revision/page never races a
//     stale paint onto the canvas or leaks a half-loaded document.
//   • A named destination is resolved against the real bytes; a page index that
//     does not exist (named or explicit) is reported honestly and we never
//     silently fall back to page 1 for a bookmark/link target. The focus
//     highlight paints ONLY on the exactly resolved target page — never on an
//     unresolved named destination or an invalid/clamped explicit page.

/* global HTMLCanvasElement, ResizeObserver, AbortController, DOMException, getSelection */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement } from 'react';
import { navStyles } from './navShared';
import './pdfTextLayer.css';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import {
  clampPage,
  clampRectToBox,
  cssPointToPdfPoint,
  fitWidthScale,
  isSelectableRect,
  pageBoxOf,
  pdfRectFromPoints,
  viewportRectToCssBox,
  type CssBox,
  type PdfPoint,
  type PdfRect,
  type ViewportLike,
} from './pdfGeometry';

/** What the editors ask the viewer to show. A bumped {@link nonce} re-applies the
 *  same focus (e.g. the reviewer clicks "View source" twice). */
export interface PdfPreviewFocus {
  /** 1-based page to show. Ignored when {@link named} resolves to a page. */
  readonly page?: number;
  /** A named destination to resolve against the bytes (overrides {@link page}). */
  readonly named?: string;
  /** A source/target rectangle (PDF user space) to highlight on the shown page. */
  readonly highlight?: PdfRect | null;
  /** Colours the highlight + caption so a link SOURCE reads differently from a
   *  jump DESTINATION. */
  readonly tone?: 'source' | 'destination';
  /** A short caption shown above the page (e.g. "Hyperlink source rectangle"). */
  readonly label?: string;
  /** Bump to force re-focus even when the other fields are unchanged. */
  readonly nonce?: number;
}

/** What the reviewer is drawing: a clickable link SOURCE rectangle, or the
 *  DESTINATION landing area/point they want the reader to arrive at. */
export type SelectionKind = 'source' | 'destination';

/** The result of a completed visual selection, in PDF user space (origin
 *  bottom-left), bound to the exact page it was drawn on. `text` is the selected
 *  text when it came from the PDF.js text layer (absent for a drawn box or a
 *  scanned page). */
export interface PdfSelectionResult {
  readonly kind: SelectionKind;
  /** 1-based page the selection was drawn on. */
  readonly page: number;
  /** Normalised PDF-space rectangle (x0<x1, y0<y1). */
  readonly rect: PdfRect;
  /** The text layer text the rectangle came from, when available. */
  readonly text?: string;
}

/** A request to put the viewer into selection mode. `nonce` lets the host
 *  re-arm the same kind after a cancel without the viewer treating it as a
 *  no-op. */
export interface PdfSelectionRequest {
  readonly kind: SelectionKind;
  readonly nonce: number;
}

export interface PdfCanvasViewerProps {
  /** blob: URL of the EXACT bytes to render (from the inline-view hook), or null. */
  readonly url: string | null;
  /** Upstream (blob fetch) loading / error, surfaced before pdf.js even starts. */
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly focus?: PdfPreviewFocus | null;
  readonly idPrefix?: string;
  /** A short heading shown above the canvas (e.g. "Revision 2"). */
  readonly caption?: string;
  /**
   * phase-compiler-six-tabs — the canonical Review surface lifts its page into
   * CompilerPage so the shell tracks it (and resets it on document change).
   * This fires ONLY for a user-initiated page move (the Prev/Next buttons) on
   * the currently-loaded document — never from a focus resolution or a render,
   * so feeding the reported page back in as `focus.page` cannot start a
   * feedback loop (the focus effect only re-applies the same page). The
   * navigation editors do not pass it, so their behaviour is unchanged.
   */
  readonly onPageChange?: (page: number) => void;
  /**
   * navigation-visual-link-selection — when set, the viewer enters visual
   * selection mode: the reviewer drags a box (or selects text) over the rendered
   * page to define a link SOURCE rectangle or a DESTINATION landing area. The
   * selection is bound to the document/revision/page it was drawn on; a stale
   * render completion or a document/page change can never apply geometry to
   * another PDF. Null/absent ⇒ the viewer is read-only (today's behaviour).
   */
  readonly selection?: PdfSelectionRequest | null;
  /** Fires on a completed, non-degenerate selection on the currently-loaded page. */
  readonly onSelect?: (result: PdfSelectionResult) => void;
  /** Fires when the reviewer cancels (Escape / pointercancel / Cancel button) —
   *  the host clears selection mode. Never fired for a committed selection. */
  readonly onSelectCancel?: () => void;
  /** When false, selection mode is DISABLED even if `selection` is set (a
   *  read-only / protected / locked / busy / policy-disabled state). The viewer
   *  shows why instead of arming the overlay. Defaults to true. */
  readonly selectionEnabled?: boolean;
  /** The reason selection is disabled, shown in place of the overlay tools. */
  readonly selectionDisabledReason?: string | null;
}

type PdfjsModule = typeof import('pdfjs-dist');
type PdfDocument = import('pdfjs-dist').PDFDocumentProxy;
type PdfLoadingTask = import('pdfjs-dist').PDFDocumentLoadingTask;
type PdfRenderTask = import('pdfjs-dist').RenderTask;

let pdfjsPromise: Promise<PdfjsModule> | null = null;
/** Load pdf.js once and point it at the BUNDLED worker. Lazy so it never lands
 *  in the main chunk; memoised so repeated opens don't re-import. A rejected
 *  import is NOT cached — the memo is reset so a retry/reopen can recover. */
async function loadPdfjs(): Promise<PdfjsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      return pdfjs;
    })().catch((err: unknown) => {
      pdfjsPromise = null;
      throw err;
    });
  }
  return pdfjsPromise;
}

const toneColors: Record<'source' | 'destination', string> = {
  source: 'var(--accent, #4f46e5)',
  destination: 'var(--ok, #15803d)',
};

const FRAME_PADDING = 16; // canvasFrame padding (8px each side).

export function PdfCanvasViewer({
  url,
  loading = false,
  error = null,
  focus = null,
  idPrefix = 'pdf-viewer',
  caption,
  onPageChange,
  selection = null,
  onSelect,
  onSelectCancel,
  selectionEnabled = true,
  selectionDisabledReason = null,
}: PdfCanvasViewerProps): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const docRef = useRef<PdfDocument | null>(null);
  const loadingTaskRef = useRef<PdfLoadingTask | null>(null);
  const renderTaskRef = useRef<PdfRenderTask | null>(null);
  const loadEpoch = useRef(0);
  const renderEpoch = useRef(0);
  // The effective scale of the last successful paint — zoom steps from HERE, so
  // the first click after "Fit width" at 0.4 goes to 0.65, not an implicit 1.25.
  const effectiveScaleRef = useRef(1);

  // The loaded document is held in STATE (not only a ref) so the render effect
  // re-runs when the bytes change even if the new revision has the same page
  // count and we are on the same page number.
  const [activeDoc, setActiveDoc] = useState<PdfDocument | null>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [scaleMode, setScaleMode] = useState<{ kind: 'fit' } | { kind: 'scale'; value: number }>({ kind: 'fit' });
  const [renderError, setRenderError] = useState<string | null>(null);
  const [destNote, setDestNote] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<CssBox | null>(null);
  // The page the current focus request resolved to, tagged with the URL and
  // focus-request identity it was resolved against. null means unresolved,
  // failed, or out-of-range — and a stale entry can never paint, because the
  // render effect checks the identity against the CURRENT url + focus key.
  const [resolvedFocus, setResolvedFocus] = useState<{ url: string; key: string; page: number } | null>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  // ---- visual selection state ------------------------------------------------
  // The viewport + identity of the LAST successful paint. Pointer/text geometry
  // is converted against THIS viewport, and a selection only commits when the
  // identity still matches the current url + page — so a stale render completion
  // or a document/page switch can never apply geometry to another PDF.
  const viewportRef = useRef<ViewportLike | null>(null);
  // Tagged with the paint GENERATION it was captured on. A visual selection only
  // commits when the stored generation still matches the one its gesture started
  // on (and a paint is READY, not mid-flight), so a zoom/resize/revision repaint
  // during the gesture can never convert against a stale viewport or a resized
  // canvas (Codex finding 2).
  const renderedIdentityRef = useRef<{ url: string; page: number; gen: number } | null>(null);
  const paintGenRef = useRef(0);
  // False from the moment a (re)paint starts until it SUCCEEDS; a commit attempted
  // while false is refused — the canvas may already be resized while the viewport
  // ref still describes the previous paint.
  const paintReadyRef = useRef(false);
  const textLayerRef = useRef<HTMLDivElement | null>(null);
  const [selectTool, setSelectTool] = useState<'box' | 'text'>('box');
  // The live drag box in CSS pixels relative to the canvas wrapper (preview only),
  // tagged with the paint generation the drag STARTED on.
  const dragStartRef = useRef<{ x: number; y: number; gen: number } | null>(null);
  const [dragBox, setDragBox] = useState<CssBox | null>(null);
  // null = not yet probed; true/false = the current page has selectable text.
  const [textAvailable, setTextAvailable] = useState<boolean | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  // Track the live container width (fit-to-width). ResizeObserver is absent in
  // some test environments → fall back to a one-shot read.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const read = (): void => setContainerWidth(Math.max(120, el.clientWidth || 600));
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url]);

  // ---- load the document from the exact bytes --------------------------------
  useEffect(() => {
    const epoch = ++loadEpoch.current;
    const controller = new AbortController();
    setHighlight(null);
    setDestNote(null);
    setRenderError(null);
    // Clear the old revision's page count on ANY url change so the indicator
    // and Next gating never show the previous document's bounds while the new
    // bytes load.
    setNumPages(null);
    // Hide/relabel: the old canvas must not read as the new revision while the
    // new bytes load. Dropping activeDoc clears the canvas via the render effect.
    setActiveDoc(null);
    if (!url) {
      setDocError(null);
      setDocLoading(false);
      return;
    }
    setDocLoading(true);
    setDocError(null);
    (async () => {
      const pdfjs = await loadPdfjs();
      if (epoch !== loadEpoch.current) return;
      const bytes = new Uint8Array(await (await fetch(url, { signal: controller.signal })).arrayBuffer());
      if (epoch !== loadEpoch.current) return;
      const task = pdfjs.getDocument({ data: bytes, isEvalSupported: false });
      loadingTaskRef.current = task;
      const doc = await task.promise;
      if (epoch !== loadEpoch.current) {
        // Superseded between resolve and here: destroy and bail.
        void doc.destroy();
        return;
      }
      loadingTaskRef.current = null;
      docRef.current = doc;
      setActiveDoc(doc);
      setLoadedUrl(url);
      setNumPages(doc.numPages);
      // Clamp the CURRENT page to the new bounds (manual-nav safety). An
      // explicit focus page/named destination is resolved honestly by the
      // focus effect — we never silently clamp a bookmark/link target here.
      setPage((p) => clampPage(p, doc.numPages));
      setDocLoading(false);
    })().catch((err: unknown) => {
      if (epoch !== loadEpoch.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setDocError(err instanceof Error ? err.message : 'Could not render this PDF.');
      setDocLoading(false);
    });
    return () => {
      // Invalidate this load's epoch so any late resolution is a no-op, then
      // tear down everything it may have started. Bumping the epoch ref in
      // cleanup IS the invalidation — copying it first would defeat the guard.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- epoch bump in cleanup is intentional invalidation
      loadEpoch.current++;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- epoch bump in cleanup is intentional invalidation
      renderEpoch.current++;
      controller.abort();
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      if (loadingTaskRef.current) {
        void loadingTaskRef.current.destroy();
        loadingTaskRef.current = null;
      }
      if (docRef.current) {
        void docRef.current.destroy();
        docRef.current = null;
      }
    };
  }, [url]);

  // ---- apply focus: resolve a named destination, else jump to the page ------
  // Identity of the current focus request. A bumped nonce must re-apply the
  // same focus, so the nonce is part of the key. The highlight paints only
  // while the stored resolution matches this key AND the current URL, so a
  // stale resolved page can never surface after the URL or focus changes.
  const focusKey = focus ? `${focus.named ?? ''} ${focus.page ?? ''} ${focus.nonce ?? ''}` : null;

  useEffect(() => {
    if (!focus || focusKey === null) {
      setResolvedFocus(null);
      return;
    }
    // Never resolve against a document that does not match the current URL —
    // during a revision switch activeDoc/loadedUrl can lag the url prop.
    if (!activeDoc || numPages === null || url === null || loadedUrl !== url) return;
    const key = focusKey;
    let cancelled = false;
    setDestNote(null);
    setResolvedFocus(null);
    if (focus.named) {
      (async () => {
        const dest = await activeDoc.getDestination(focus.named as string);
        if (cancelled) return;
        if (!dest || dest.length === 0) {
          setDestNote(`The named destination “${focus.named}” was not found in these bytes.`);
          return;
        }
        // The first element is either an explicit integer page index or an
        // object reference we resolve to one.
        const first = dest[0] as unknown;
        const pageIndex =
          typeof first === 'number' ? first : await activeDoc.getPageIndex(first as never);
        if (cancelled) return;
        const target = pageIndex + 1;
        if (!Number.isInteger(target) || target < 1 || target > numPages) {
          setDestNote(`The named destination “${focus.named}” points outside this document.`);
          return;
        }
        setPage(target);
        setResolvedFocus({ url, key, page: target });
      })().catch(() => {
        if (!cancelled) setDestNote(`The named destination “${focus.named}” could not be resolved.`);
      });
    } else if (typeof focus.page === 'number') {
      // An explicit bookmark/link page: if it does not exist we say so rather
      // than silently clamping onto a different page.
      if (!Number.isInteger(focus.page) || focus.page < 1 || focus.page > numPages) {
        setDestNote(`This target points to page ${focus.page}, which is outside this ${numPages}-page document.`);
      } else {
        setPage(focus.page);
        setResolvedFocus({ url, key, page: focus.page });
      }
    }
    return () => {
      cancelled = true;
    };
  }, [focus, focusKey, activeDoc, numPages, loadedUrl, url]);

  // ---- render the current page ----------------------------------------------
  const scaleFor = useCallback(
    (viewportWidthAtScale1: number): number =>
      scaleMode.kind === 'fit'
        ? fitWidthScale(viewportWidthAtScale1, containerWidth - FRAME_PADDING)
        : scaleMode.value,
    [scaleMode, containerWidth],
  );

  useEffect(() => {
    const doc = activeDoc;
    const canvas = canvasRef.current;
    const epoch = ++renderEpoch.current;
    renderTaskRef.current?.cancel();
    renderTaskRef.current = null;
    setRenderError(null);
    setHighlight(null);
    // A (re)paint has begun: the viewport/canvas are about to change, so no
    // selection may commit against them until it SUCCEEDS, and any in-progress
    // box drag is discarded (its CSS coords belonged to the previous paint).
    paintReadyRef.current = false;
    if (dragStartRef.current) {
      dragStartRef.current = null;
      setDragBox(null);
    }
    if (!canvas) return;
    if (!doc || loadedUrl !== url || numPages === null) {
      // No document (e.g. a revision change is loading): clear the old pixels so
      // the stale page never reads as the new revision.
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    (async () => {
      const pdfPage = await doc.getPage(clampPage(page, numPages));
      if (epoch !== renderEpoch.current || doc !== activeDoc) return;
      const base = pdfPage.getViewport({ scale: 1 });
      const scale = scaleFor(base.width);
      const viewport = pdfPage.getViewport({ scale });
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setRenderError('This browser did not provide a 2D canvas context, so the page cannot be drawn.');
        return;
      }
      effectiveScaleRef.current = scale;
      const dpr = typeof globalThis.devicePixelRatio === 'number' ? globalThis.devicePixelRatio : 1;
      canvas.width = Math.ceil(viewport.width * dpr);
      canvas.height = Math.ceil(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      // Clear any prior page before painting the new one.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const task = pdfPage.render({
        canvas,
        canvasContext: ctx,
        viewport,
        ...(dpr !== 1 ? { transform: [dpr, 0, 0, dpr, 0, 0] } : {}),
      });
      renderTaskRef.current = task;
      await task.promise;
      if (epoch !== renderEpoch.current || doc !== activeDoc) return;
      renderTaskRef.current = null;
      // Record the viewport + identity this paint used so a visual selection
      // converts its pointer/text geometry against the EXACT page shown and can
      // verify (url, page) still match before committing.
      viewportRef.current = viewport as unknown as ViewportLike;
      paintGenRef.current += 1;
      paintReadyRef.current = true;
      if (url !== null) renderedIdentityRef.current = { url, page, gen: paintGenRef.current };
      // Highlight only when the focus rectangle belongs to the page we drew:
      // the resolved target page — identity-checked against the CURRENT url
      // and focus request — must be exactly this page (1-based, no clamping).
      // An unresolved named destination or an invalid explicit page resolves
      // to null and never paints. A focus with no page/named target highlights
      // whatever page is shown.
      if (focus?.highlight) {
        const targetPage =
          focus.named == null && focus.page == null
            ? page
            : resolvedFocus !== null && resolvedFocus.url === url && resolvedFocus.key === focusKey
              ? resolvedFocus.page
              : null;
        if (targetPage === page) {
          const r: PdfRect = focus.highlight;
          const vpRect = viewport.convertToViewportRectangle([r.x0, r.y0, r.x1, r.y1]);
          setHighlight(viewportRectToCssBox(vpRect, viewport.width, viewport.height));
        }
      }
    })().catch((err: unknown) => {
      if (epoch !== renderEpoch.current) return;
      // A cancelled render is expected on supersede — not an error to show.
      const name = err instanceof Error ? err.name : '';
      if (name === 'RenderingCancelledException') return;
      setRenderError(err instanceof Error ? err.message : 'Could not render this page.');
    });
    return () => {
      // getPage may still be pending when this effect is replaced or unmounted.
      // Invalidate it before cancelling a task that may already be painting.
      // The epoch ref bump in cleanup IS the invalidation mechanism.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- epoch bump in cleanup is intentional invalidation
      renderEpoch.current++;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
    };
  }, [activeDoc, loadedUrl, url, page, numPages, scaleFor, focus, focusKey, resolvedFocus]);

  const zoomTo = useCallback((delta: number) => {
    setScaleMode(() => ({ kind: 'scale', value: Math.max(0.25, Math.min(4, effectiveScaleRef.current + delta)) }));
  }, []);

  const tone = focus?.tone ?? 'source';
  const highlightColor = toneColors[tone];

  // phase-compiler-six-tabs correction — page navigation (and the onPageChange
  // report it emits) is valid ONLY for the document whose bytes are currently
  // loaded and measured. While a new selection's bytes load, `loadedUrl` lags
  // `url` and `numPages` is null; a click in that window would otherwise move
  // the stale `page` and report a PREVIOUS document's page into the newly
  // selected one. Gate both buttons (and their handlers) on readiness, which
  // also disables Next while the count is unknown.
  const pageNavReady = activeDoc !== null && loadedUrl === url && numPages !== null;

  // =====================================================================
  // Visual selection (navigation-visual-link-selection)
  // =====================================================================
  // Armed only when the host requests a selection, it is enabled, and a page is
  // actually painted. Everything below is inert when `selectionActive` is false,
  // so the read-only viewer behaves exactly as before.
  const selectionActive = selection !== null && selectionEnabled && pageNavReady;
  const selectionKind = selection?.kind ?? null;

  // Reset the transient drag/preview + probe state whenever the page, document,
  // tool, or the selection request identity changes — so a gesture can never
  // straddle two pages and a re-arm starts clean.
  useEffect(() => {
    dragStartRef.current = null;
    setDragBox(null);
    setSelectionError(null);
  }, [page, url, selectTool, selection?.nonce, selectionActive]);

  // Commit a CSS-space selection (relative to the canvas top-left) as a PDF-space
  // rectangle, bound to the exact page it was drawn on. Rejects a stale identity
  // and a degenerate gesture; clamps to the page box (consistent with the server
  // on-page requirement). `getBoundingClientRect` gives the live CSS size, so a
  // DPR/zoom-scaled canvas maps correctly.
  const commitCssSelection = useCallback(
    (p0x: number, p0y: number, p1x: number, p1y: number, text?: string, expectedGen?: number): void => {
      if (selectionKind === null) return;
      const vp = viewportRef.current;
      const id = renderedIdentityRef.current;
      const canvas = canvasRef.current;
      // A SUCCESSFUL paint must back the conversion: while one is in flight (or
      // has not completed yet) the canvas may already be sized though the viewport
      // ref is absent or describes the previous paint, so converting now would mix
      // mismatched geometry. Refuse honestly rather than commit a stale rect
      // (Codex finding 2).
      if (!paintReadyRef.current || !vp || !id || !canvas) {
        setSelectionError('The page is still rendering — try again.');
        return;
      }
      // Stale-render / page-switch / mid-gesture-repaint guard: the paint we
      // convert against MUST still be the current url + page AND the same paint
      // generation the gesture started on, or we refuse rather than map onto a
      // stale/mismatched paint.
      if (id.url !== url || id.page !== page || (expectedGen !== undefined && id.gen !== expectedGen)) {
        setSelectionError('The view changed before the selection finished — try again.');
        return;
      }
      const box = canvas.getBoundingClientRect();
      const a: PdfPoint | null = cssPointToPdfPoint(p0x, p0y, box.width, box.height, vp);
      const b: PdfPoint | null = cssPointToPdfPoint(p1x, p1y, box.width, box.height, vp);
      if (!a || !b) {
        setSelectionError('Could not map that selection onto the page.');
        return;
      }
      let rect: PdfRect | null = pdfRectFromPoints(a, b);
      const pageBox = pageBoxOf(vp);
      if (rect && pageBox) rect = clampRectToBox(rect, pageBox);
      if (!isSelectableRect(rect)) {
        setSelectionError('That selection was too small — draw a larger area.');
        return;
      }
      setSelectionError(null);
      setDragBox(null);
      dragStartRef.current = null;
      onSelect?.({ kind: selectionKind, page, rect: rect as PdfRect, ...(text && text.trim() ? { text: text.trim() } : {}) });
    },
    [onSelect, page, url, selectionKind],
  );

  // ---- box-draw pointer handlers (CSS coords relative to the canvas) ----
  const onBoxPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!selectionActive || selectTool !== 'box') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const box = canvas.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    dragStartRef.current = { x, y, gen: paintGenRef.current };
    setDragBox({ left: x, top: y, width: 0, height: 0 });
    setSelectionError(null);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom lacks capture */ }
  }, [selectionActive, selectTool]);

  const onBoxPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    const start = dragStartRef.current;
    if (!start) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const box = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(box.width, e.clientX - box.left));
    const y = Math.max(0, Math.min(box.height, e.clientY - box.top));
    setDragBox({ left: Math.min(start.x, x), top: Math.min(start.y, y), width: Math.abs(x - start.x), height: Math.abs(y - start.y) });
  }, []);

  const onBoxPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    const start = dragStartRef.current;
    if (!start) return;
    const canvas = canvasRef.current;
    dragStartRef.current = null;
    if (!canvas) { setDragBox(null); return; }
    const box = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(box.width, e.clientX - box.left));
    const y = Math.max(0, Math.min(box.height, e.clientY - box.top));
    commitCssSelection(start.x, start.y, x, y, undefined, start.gen);
  }, [commitCssSelection]);

  const cancelSelection = useCallback((): void => {
    dragStartRef.current = null;
    setDragBox(null);
    setSelectionError(null);
    onSelectCancel?.();
  }, [onSelectCancel]);

  const onBoxPointerCancel = useCallback((): void => {
    // A cancelled pointer (e.g. the system interrupts the drag) discards the
    // in-progress box WITHOUT exiting selection mode — the reviewer can retry.
    dragStartRef.current = null;
    setDragBox(null);
  }, []);

  // ---- text-layer selection: on mouseup, map the DOM selection's bounding box
  // to a PDF rect and commit the selected text. ----
  const onTextLayerMouseUp = useCallback((): void => {
    if (!selectionActive || selectTool !== 'text') return;
    const sel = typeof getSelection === 'function' ? getSelection() : null;
    const canvas = canvasRef.current;
    const layer = textLayerRef.current;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !canvas || !layer) return;
    const range = sel.getRangeAt(0);
    // Only honour a selection that lives inside THIS page's text layer — a range
    // whose anchor drifted out (e.g. a drag released off the layer) is ignored.
    if (!layer.contains(range.commonAncestorContainer)) return;
    // A phrase spanning multiple lines commits its UNION bounding box (the
    // documented design — the clickable source is the phrase's extent, not each
    // glyph). `getBoundingClientRect` gives exactly that union.
    const r = range.getBoundingClientRect();
    if (!r || (r.width === 0 && r.height === 0)) return;
    const box = canvas.getBoundingClientRect();
    const text = sel.toString();
    // Clear the DOM selection AFTER reading it, so a later mouseup cannot
    // re-commit this now-consumed range (Kimi C7).
    try { sel.removeAllRanges(); } catch { /* jsdom/older browsers */ }
    commitCssSelection(r.left - box.left, r.top - box.top, r.right - box.left, r.bottom - box.top, text);
  }, [selectionActive, selectTool, commitCssSelection]);

  // Escape cancels selection mode entirely (matches the workspace's Escape-to-
  // close expectation). Bound only while a selection is armed.
  useEffect(() => {
    if (!selectionActive) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cancelSelection();
      }
    };
    globalThis.document.addEventListener('keydown', onKey, true);
    return () => globalThis.document.removeEventListener('keydown', onKey, true);
  }, [selectionActive, cancelSelection]);

  // ---- text layer rendering (only while the text tool is armed) ----
  useEffect(() => {
    const container = textLayerRef.current;
    if (container) container.replaceChildren();
    if (!selectionActive || selectTool !== 'text' || !activeDoc || numPages === null || loadedUrl !== url) {
      setTextAvailable(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const pdfjs = await loadPdfjs();
      if (cancelled) return;
      const pdfPage = await activeDoc.getPage(clampPage(page, numPages));
      if (cancelled) return;
      const base = pdfPage.getViewport({ scale: 1 });
      const scale = scaleFor(base.width);
      const viewport = pdfPage.getViewport({ scale });
      const textContent = await pdfPage.getTextContent();
      if (cancelled) return;
      const items = (textContent as { items?: unknown[] } | null)?.items ?? [];
      if (items.length === 0) {
        setTextAvailable(false);
        return;
      }
      const TextLayerCtor = (pdfjs as unknown as { TextLayer?: new (o: unknown) => { render: () => Promise<void> } }).TextLayer;
      const cont = textLayerRef.current;
      if (typeof TextLayerCtor !== 'function' || !cont) {
        // No text-layer API in this build: be honest, offer the box instead.
        setTextAvailable(false);
        return;
      }
      // pdf.js positions every span with `calc(var(--total-scale-factor) * …)`
      // and sizes the CONTAINER itself via setLayerDimensions using the same var
      // (plus CSS `round(…, var(--scale-round-x))`). Set all four BEFORE
      // constructing the TextLayer so the span geometry and the container size
      // resolve; do NOT hard-set width/height here — setLayerDimensions owns them,
      // keeping the layer exactly aligned with the canvas at any zoom.
      //
      // CRITICAL: `--total-scale-factor` is the canvas' EFFECTIVE scale, which is
      // `viewport.scale × userUnit` — pdf.js' PageViewport multiplies the scale by
      // userUnit for width/height and the render transform, but exposes
      // `viewport.scale` WITHOUT it. Using `scale` alone put the text layer at the
      // wrong scale on large-format (userUnit≠1) pages (Codex finding #20, proven
      // in browser). `--scale-factor` stays the bare CSS zoom.
      const userUnit = (viewport as unknown as { userUnit?: number }).userUnit ?? 1;
      cont.style.setProperty('--scale-factor', String(scale));
      cont.style.setProperty('--total-scale-factor', String(scale * userUnit));
      cont.style.setProperty('--scale-round-x', '1px');
      cont.style.setProperty('--scale-round-y', '1px');
      try {
        const tl = new TextLayerCtor({ textContentSource: textContent, container: cont, viewport });
        await tl.render();
        if (cancelled) return;
        setTextAvailable(true);
      } catch {
        if (!cancelled) setTextAvailable(false);
      }
    })().catch(() => {
      if (!cancelled) setTextAvailable(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectionActive, selectTool, activeDoc, page, numPages, scaleFor, loadedUrl, url]);

  if (loading) {
    return (
      <div style={navStyles.muted} data-testid={`${idPrefix}-loading`}>
        Loading the exact bytes…
      </div>
    );
  }
  if (error) {
    return (
      <div style={navStyles.error} role="alert" data-testid={`${idPrefix}-upstream-error`}>
        {error}
      </div>
    );
  }
  if (!url) {
    return (
      <div style={navStyles.muted} data-testid={`${idPrefix}-empty`}>
        No document to preview.
      </div>
    );
  }

  return (
    <div data-testid={`${idPrefix}-root`} style={viewerShell}>
      <div style={controlsRow}>
        <button
          type="button"
          style={!pageNavReady || page <= 1 ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
          data-testid={`${idPrefix}-prev`}
          disabled={!pageNavReady || page <= 1}
          onClick={() => {
            if (!pageNavReady) return;
            const next = Math.max(1, page - 1);
            setPage(next);
            onPageChange?.(next);
          }}
        >
          ‹ Prev
        </button>
        <span style={navStyles.muted} data-testid={`${idPrefix}-page-indicator`}>
          Page {page}
          {numPages !== null ? ` of ${numPages}` : ''}
        </span>
        <button
          type="button"
          style={!pageNavReady || (numPages !== null && page >= numPages) ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
          data-testid={`${idPrefix}-next`}
          disabled={!pageNavReady || (numPages !== null && page >= numPages)}
          onClick={() => {
            if (!pageNavReady || numPages === null) return;
            const next = Math.min(numPages, page + 1);
            setPage(next);
            onPageChange?.(next);
          }}
        >
          Next ›
        </button>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} aria-hidden />
        <button type="button" style={navStyles.secondaryBtn} data-testid={`${idPrefix}-fit`} onClick={() => setScaleMode({ kind: 'fit' })}>
          Fit width
        </button>
        <button type="button" style={navStyles.secondaryBtn} data-testid={`${idPrefix}-zoom-out`} aria-label="Zoom out" onClick={() => zoomTo(-0.25)}>
          −
        </button>
        <button type="button" style={navStyles.secondaryBtn} data-testid={`${idPrefix}-zoom-in`} aria-label="Zoom in" onClick={() => zoomTo(0.25)}>
          +
        </button>
      </div>

      {caption && (
        <div style={{ ...navStyles.label, marginTop: 4 }} data-testid={`${idPrefix}-caption`}>
          {caption}
        </div>
      )}
      {focus?.label && (
        <div style={{ ...navStyles.muted, color: highlightColor }} data-testid={`${idPrefix}-focus-label`}>
          {tone === 'source' ? 'Source' : 'Destination'}: {focus.label}
        </div>
      )}
      {destNote && (
        <div style={{ ...navStyles.note, color: 'var(--warning, #b45309)' }} data-testid={`${idPrefix}-dest-note`}>
          {destNote}
        </div>
      )}

      {/* ---- visual-selection control bar ---- */}
      {selection !== null && !selectionEnabled && (
        <div style={{ ...navStyles.note, color: 'var(--text-muted)' }} data-testid={`${idPrefix}-selection-disabled`}>
          Selection is unavailable: {selectionDisabledReason ?? 'editing is disabled for this artifact.'}
        </div>
      )}
      {selectionActive && (
        <div style={{ ...navStyles.toolbar, borderColor: 'var(--accent, #4f46e5)' }} data-testid={`${idPrefix}-selection-bar`}>
          <span style={{ ...navStyles.label, color: 'var(--accent, #4f46e5)' }}>
            {selectionKind === 'source' ? 'Draw the link source' : 'Mark where the reader lands'}
          </span>
          <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} aria-hidden />
          <button
            type="button"
            data-testid={`${idPrefix}-select-tool-box`}
            aria-pressed={selectTool === 'box'}
            style={selectTool === 'box' ? navStyles.button : navStyles.secondaryBtn}
            onClick={() => setSelectTool('box')}
          >
            Draw box
          </button>
          <button
            type="button"
            data-testid={`${idPrefix}-select-tool-text`}
            aria-pressed={selectTool === 'text'}
            style={selectTool === 'text' ? navStyles.button : navStyles.secondaryBtn}
            onClick={() => setSelectTool('text')}
          >
            Select text
          </button>
          <span style={navStyles.muted} data-testid={`${idPrefix}-select-hint`}>
            {selectTool === 'box'
              ? 'Drag a rectangle over the page.'
              : 'Select a word or phrase; its bounding box is used.'}
          </span>
          <button type="button" data-testid={`${idPrefix}-select-cancel`} style={navStyles.secondaryBtn} onClick={cancelSelection}>
            Cancel
          </button>
        </div>
      )}
      {selectionActive && selectTool === 'text' && textAvailable === false && (
        <div style={{ ...navStyles.note, color: 'var(--warning, #b45309)' }} data-testid={`${idPrefix}-text-unavailable`}>
          This page has no selectable text (it may be scanned or image-only). Use “Draw box” to
          select the area directly.
        </div>
      )}
      {selectionError && (
        <div style={{ ...navStyles.error }} role="alert" data-testid={`${idPrefix}-selection-error`}>
          {selectionError}
        </div>
      )}

      <div ref={containerRef} style={canvasFrame} data-testid={`${idPrefix}-canvas-frame`}>
        {docLoading && (
          <div style={navStyles.muted} data-testid={`${idPrefix}-rendering`}>
            Rendering…
          </div>
        )}
        {docError && (
          <div style={navStyles.error} role="alert" data-testid={`${idPrefix}-doc-error`}>
            Could not render this PDF: {docError}
          </div>
        )}
        {renderError && !docError && (
          <div style={navStyles.error} role="alert" data-testid={`${idPrefix}-render-error`}>
            {renderError}
          </div>
        )}
        <div style={{ position: 'relative', display: docError || loadedUrl !== url ? 'none' : 'inline-block' }}>
          {/* Identity is checked during render, before effects can clear old pixels. */}
          <canvas ref={canvasRef} role="img" aria-label={`PDF page ${page}${numPages === null ? '' : ` of ${numPages}`}`} data-testid={`${idPrefix}-canvas`} style={{ display: 'block' }} />
          {highlight && (
            <div
              aria-hidden
              data-testid={`${idPrefix}-highlight`}
              style={{
                position: 'absolute',
                left: highlight.left,
                top: highlight.top,
                width: highlight.width,
                height: highlight.height,
                border: `2px solid ${highlightColor}`,
                background: tone === 'source' ? 'rgba(79,70,229,0.15)' : 'rgba(21,128,61,0.15)',
                pointerEvents: 'none',
              }}
            />
          )}
          {/* Text layer (selectable spans) — present only while the text tool is
              armed. The .navPdfTextLayer class carries the pdf.js-required span
              rules (absolute, transform-origin 0 0, white-space pre, transparent),
              without which the spans ghost as inline text at the top-left. pdf.js
              sizes the container (setLayerDimensions); we only toggle visibility. */}
          <div
            ref={textLayerRef}
            className="navPdfTextLayer"
            data-testid={`${idPrefix}-text-layer`}
            onMouseUp={onTextLayerMouseUp}
            style={{ display: selectionActive && selectTool === 'text' ? 'block' : 'none', userSelect: 'text' }}
          />
          {/* Box-draw overlay — captures pointer drags only while the box tool is
              armed; a cross-hair cursor signals the mode. */}
          {selectionActive && selectTool === 'box' && (
            <div
              data-testid={`${idPrefix}-box-overlay`}
              onPointerDown={onBoxPointerDown}
              onPointerMove={onBoxPointerMove}
              onPointerUp={onBoxPointerUp}
              onPointerCancel={onBoxPointerCancel}
              style={{ position: 'absolute', inset: 0, cursor: 'crosshair', touchAction: 'none' }}
            >
              {dragBox && dragBox.width > 0 && dragBox.height > 0 && (
                <div
                  aria-hidden
                  data-testid={`${idPrefix}-drag-box`}
                  style={{
                    position: 'absolute',
                    left: dragBox.left,
                    top: dragBox.top,
                    width: dragBox.width,
                    height: dragBox.height,
                    border: `2px dashed ${selectionKind === 'source' ? toneColors.source : toneColors.destination}`,
                    background: selectionKind === 'source' ? 'rgba(79,70,229,0.12)' : 'rgba(21,128,61,0.12)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const viewerShell: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 };
const controlsRow: CSSProperties = { ...navStyles.toolbar };
const canvasFrame: CSSProperties = {
  marginTop: 4,
  padding: 8,
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface2)',
  overflow: 'auto',
  maxHeight: 520,
};

export default PdfCanvasViewer;
